import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';

// Generate a unique Student ID or Employee ID
const generateUniqueId = (userType, uid) => {
  // Use first 6 chars of UID (uppercase) + timestamp suffix for uniqueness
  const shortUid = uid.substring(0, 6).toUpperCase();
  const timestamp = Date.now().toString().slice(-4);
  if (userType === 'jobseeker') {
    return `STU-${shortUid}${timestamp}`;
  } else if (userType === 'employer') {
    return `EMP-${shortUid}${timestamp}`;
  }
  return null;
};

// Register a new user
export const registerUser = async ({ email, password, firstName, lastName, userType, phone, referredBy }) => {
  try {
    // Create user with Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const uid = userCredential.user.uid;

    // Create user document in Firestore
    const userDoc = {
      firstName,
      lastName,
      email,
      phone,
      userType,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Add referredBy field if it exists
    if (referredBy) {
      userDoc.referredBy = referredBy;
      userDoc.franchiseId = referredBy; // Also set franchiseId directly from referral
    }

    // Generate Student ID or Employee ID
    const uniqueId = generateUniqueId(userType, uid);
    if (uniqueId) {
      if (userType === 'jobseeker') {
        userDoc.studentId = uniqueId;
      } else if (userType === 'employer') {
        userDoc.employeeId = uniqueId;
      }
    }

    await setDoc(doc(db, 'users', uid), userDoc);
    return { success: true, uid };
  } catch (error) {
    console.error('Registration error:', error);
    return { success: false, error: error.message };
  }
};

// Login user
export const loginUser = async ({ email, password }) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { success: true, user: userCredential.user };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, error: error.message };
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    return { success: false, error: error.message };
  }
};

// Get user data from Firestore
export const getUserData = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return { success: true, data: userDoc.data() };
    } else {
      return { success: false, error: 'User not found' };
    }
  } catch (error) {
    console.error('Get user data error:', error);
    return { success: false, error: error.message };
  }
};

// Update user data
export const updateUserData = async (uid, userData) => {
  try {
    await updateDoc(doc(db, 'users', uid), {
      ...userData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update user data error:', error);
    return { success: false, error: error.message };
  }
};

// Auth state change listener
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Get all users from Firestore
export const getAllUsers = async () => {
  try {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, users };
  } catch (error) {
    console.error('Get all users error:', error);
    return { success: false, error: error.message, users: [] };
  }
};

// Delete user from Firestore and Firebase Auth
export const deleteUser = async (uid) => {
  try {
    // Delete user document from Firestore
    await deleteDoc(doc(db, 'users', uid));

    // Delete user from Firebase Auth using Cloud Function
    // Note: Deleting from Firebase Auth requires admin privileges
    // This should be done through a Cloud Function
    try {
      const functions = getFunctions();
      const deleteUserAuth = httpsCallable(functions, 'deleteUserAuth');
      await deleteUserAuth({ uid });
    } catch (authError) {
      console.error('Error deleting user from Auth (requires Cloud Function):', authError);
      // Continue even if auth deletion fails - at least Firestore is cleaned up
    }

    return { success: true };
  } catch (error) {
    console.error('Delete user error:', error);
    return { success: false, error: error.message };
  }
};